import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const { t } = useTranslation("auth");
  const translation = t('verify_email_tran');

  const { email } = location.state || {};
  const [emailAdd, setEmailAdd] = useState(email)
  const [resendCode, setResendCode] = useState(false)

  useEffect(() => {
    if (email) { 
      setResendCode(true);
      setTimeLeft(120);
    }
  }, [email]); 

  useEffect(() => {
    if (!resendCode || timeLeft <= 0) return;
    const timerId = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timerId);
  }, [resendCode, timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!emailAdd) {
      setError(translation.errors.email_required);
      return;
    }
  
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_DOMAIN}/user/verify-email-through-code/`,
        { "code": code, "email": emailAdd },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
  
      if (response.status === 200) {
        setError('')
        setSuccessMessage(translation.success_messages.verified);
        setResendCode(false);
        setTimeLeft(0)
        setTimeout(() => {
          navigate('/auth/login');
        }, 5000);
      }
    } catch (err) {
      setError(err.response?.data?.error || translation.errors.verification_failed);
      setTimeLeft(0);
      setResendCode(false)
    } finally {
      setIsLoading(false);
    }
  };

  const ResendVerificationCode = async (e) => {
    e.preventDefault();
    if (!emailAdd) {
      setError(translation.errors.email_required);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_DOMAIN}/user/resend-email-verification-code/`,
        { "email": emailAdd },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setError('')
        setSuccessMessage(response?.data?.success || translation.success_messages.code_resent);
        setTimeLeft(120);
        setResendCode(true)
        
        setTimeout(() => {
          setSuccessMessage('')
        }, 5000)
      }
    } catch (err) {
      setError(err.response?.data?.error || translation.errors.resend_failed);
      setTimeLeft(0);
      setResendCode(false)
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 mx-5 rounded-lg m-5">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-gray-50 dark:bg-gray-600 py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                {translation.heading}
              </h2>
              <p className="mt-2 text-sm text-gray-900 dark:text-white">
                {t('common.we_sent_code')} <span className="font-medium text-indigo-600 dark:text-blue-300">{emailAdd}</span>
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label 
                  htmlFor="email" 
                  className="block mb-2 font-bold text-black dark:text-stone-50">
                  {translation.email_label}
                </label>

                <input 
                  type="email" 
                  name="email" 
                  id="email" 
                  placeholder={translation.email_placeholder}
                  className="az-input-btn" 
                  required 
                  onChange={(e)=>{setEmailAdd(e.target.value)}}
                  value={emailAdd}
                />
              </div>
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 dark:text-white">
                  {translation.code_label}
                </label>
                <div className="mt-1">
                  <input
                    id="code"
                    name="code"
                    type="text"
                    required
                    value={code}
                    className="az-input-btn"
                    placeholder={translation.code_placeholder}
                    disabled={!emailAdd}
                    maxLength={4}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*$/.test(val)) {
                        setCode(val);
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={!emailAdd || isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stone-500 disabled:opacity-50 disabled:cursor-not-allowed bg-blue-500"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {translation.verifying_text}
                    </>
                  ) : (
                    translation.verify_button
                  )}
                </button>
              </div>

              {error && (
                <p className="flex justify-center bg-red text-white rounded-lg font-bold text-sm p-2">
                  {error}
                </p>
              )}

              {successMessage && (
                <p className="flex justify-center bg-green-700 text-white rounded-lg font-bold text-sm p-2">
                  {successMessage}
                </p>
              )}

              {!emailAdd && (
                <p className="flex justify-center bg-red text-white rounded-lg font-bold text-sm p-2">
                  {translation.errors.email_missing}
                </p>
              )}

              <div className="mt-6 text-center text-sm">
                <p className="text-gray-600 dark:text-white">
                  {translation.no_code_text}{' '}
                  {timeLeft > 0 ? (
                    <span className="text-gray-500 dark:text-white">
                      {translation.resend_timer.replace('{time}', formatTime(timeLeft))}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="font-bold text-indigo-600 hover:text-indigo-500 disabled:opacity-50 dark:text-blue-400"
                      onClick={ResendVerificationCode}
                      disabled={isLoading}
                    >
                      {translation.resend_button}
                    </button>
                  )}
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerifyEmail;